import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * Header cart icon with Joan's custom item count.
 *
 * A fabric line marked with _joans_fabric counts as one item,
 * regardless of its underlying 10 cm quantity.
 *
 * All other products count by their actual quantity.
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent =
      value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    window.addEventListener('pageshow', this.onPageShow);

    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    window.removeEventListener('pageshow', this.onPageShow);
  }

  onPageShow = (event) => {
    if (event.persisted) {
      this.refreshJoansCartCount(false);
    }
  };

  onCartUpdate = (event) => {
    event.promise
      ?.then(() => {
        return this.refreshJoansCartCount(true);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-icon] Cart update promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Fetch Shopify's current cart and calculate Joan's
   * customer-facing count.
   *
   * @param {boolean} animate
   */
  refreshJoansCartCount = async (animate = true) => {
    try {
      const cartRoot = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${cartRoot}cart.js`, {
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error('Unable to read the current cart.');
      }

      const cart = await response.json();

      const joansCount = cart.items.reduce((count, item) => {
        const isFabric =
          item.properties?._joans_fabric === 'true' ||
          item.properties?._joans_fabric === true;

        return count + (isFabric ? 1 : item.quantity);
      }, 0);

      await this.renderCartBubble(joansCount, animate);
    } catch (error) {
      console.warn(
        '[cart-icon] Could not calculate Joan’s cart count:',
        error
      );
    }
  };

  /**
   * Renders the customer-facing cart bubble.
   *
   * @param {number} itemCount
   * @param {boolean} [animate=true]
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle(
      'hidden',
      itemCount === 0
    );

    this.refs.cartBubble.classList.toggle(
      'visually-hidden',
      itemCount === 0
    );

    this.currentCartCount = itemCount;

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      itemCount > 0
    );

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) {
      return;
    }

    await new Promise((resolve) =>
      requestAnimationFrame(resolve)
    );

    this.refs.cartBubble.classList.add(
      'cart-bubble--animating'
    );

    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove(
      'cart-bubble--animating'
    );
  };

  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) {
      return;
    }

    /*
     * Always verify against the actual cart. Session storage
     * may contain Horizon's previous raw quantity count.
     */
    this.refreshJoansCartCount(false);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}